import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useIsMobileSize } from '@/hooks';
import { useSelector } from '@/redux/helper';
import { selectHasGuidedExperienceFeature } from '@/redux/selectors/users';
import { goToStep } from '@/redux/slices/guidedTour/actions';

import FeedbackModal from './FeedbackModal';
import MobileWarningModal from './MobileWarningModal';
import WelcomeModal from './WelcomeModal';

const MASCOT_SRC = 'https://zoolife.tv/assets/guidedTour/mascot/Talking_cropped.png';
const MAP_SRC = 'https://zoolife.tv/assets/guidedTour/guided-tour-map-bg-transparent.png';

// Mounted outside Router (src/index.js), so useGuidedTour/useRouter are unavailable here.
const GuidedTourOrchestrator = () => {
  const dispatch = useDispatch();
  const isMobile = useIsMobileSize();
  const isLoggedIn = useSelector((state) => state.user.logged);
  const hasActiveL1Plan = useSelector(selectHasGuidedExperienceFeature);
  const guidedTour = useSelector((state) => state.guidedTour);

  const handleWelcomeStart = useCallback(() => {
    dispatch(goToStep('Home 1'));
  }, [dispatch]);

  const handleFeedbackSubmit = useCallback(() => {
    dispatch(goToStep('modal-end-submitted'));
  }, [dispatch]);
  // Go to End2 on close so the post-tour banner can still render while L1 is active.
  const handleFeedbackClose = useCallback(() => {
    dispatch(goToStep('End2'));
  }, [dispatch]);

  if (!isLoggedIn) return null;
  if (!hasActiveL1Plan) return null;
  // On mobile, always show the mobile warning — never the desktop modals,
  // even if isActive is true from a persisted desktop session.
  if (isMobile) return <MobileWarningModal />;
  if (!guidedTour?.isActive) return null;

  const currentStepId: string | undefined = guidedTour.stepHistory?.[guidedTour.stepHistory.length - 1];

  if (currentStepId === '0-modal') {
    return <WelcomeModal onStart={handleWelcomeStart} mascotSrc={MASCOT_SRC} mapSrc={MAP_SRC} />;
  }

  if (currentStepId === 'modal-end' || currentStepId === 'modal-end-submitted') {
    return (
      <FeedbackModal
        isSubmitted={currentStepId === 'modal-end-submitted'}
        onRatingSubmit={handleFeedbackSubmit}
        onFeedbackComplete={handleFeedbackClose}
      />
    );
  }

  return null;
};

export default GuidedTourOrchestrator;
